import { logger } from '@/lib'
import { AppOpenEntry } from '@/interfaces/analytics'

const SESSION_STORAGE_KEY = 'cwAnalyticsSession'
// Routes where a token exchange is in progress (auth-link landing page, the sign-in and
// registration waiting pages). The browsing context may still hold the PREVIOUS registrant's
// cookie there, so evaluating app_open on these routes would attribute the open to the previous
// registrant (found in local testing 2026-09-08). The dashboard mount that follows the exchange is
// where the open belongs.
const AUTH_EXCHANGE_ROUTES = ['/registrant/authorization', '/registrant/sign-in', '/registrant/registration']
const AUTH_LINK_ENTRY_KEY = 'cwAnalyticsAuthLinkEntry'
const INACTIVITY_FLOOR_MS = 30 * 60 * 1000
const IS_STANDALONE_TIMEOUT_MS = 3000
const IS_STANDALONE_POLL_MS = 300

interface SessionRecord {
  registrantId: number
  lastSeenAt: number
}

export interface AppOpenDecision {
  shouldFire: boolean
  gapSeconds: number | null
}

// Behavioral session guard (CW2-648 §4.3). Auth sessions never end in this
// app, so "session" is defined by inactivity: sessionStorage is per-tab and
// dies with the browsing context, which is exactly what makes rule 1
// ("closed the dashboard, came back at ANY time gap") work.
export class AnalyticsSessionService {
  // one-shot marker for a web-push-notification click — consumed by the
  // app_open evaluation it triggers, never stored beyond it (§4.2)
  private static pushEntryPending = false

  public static evaluateOpen(registrantId: number): AppOpenDecision {
    try {
      const record = this.readRecord()
      const now = Date.now()

      // rule 1: fresh browsing context — fires at any time gap
      if (!record) {
        this.writeRecord({ registrantId, lastSeenAt: now })
        return { shouldFire: true, gapSeconds: null }
      }

      // rule 2: the registrant changed in this browser — a new session
      // regardless of the gap (gapSeconds null so reporting counts it as a
      // session start at any threshold T)
      if (record.registrantId !== registrantId) {
        this.writeRecord({ registrantId, lastSeenAt: now })
        return { shouldFire: true, gapSeconds: null }
      }

      // rule 3: still-living context foregrounded after >= 30 min (the
      // emission floor); below the floor nothing fires or is stored
      const gapMs = now - record.lastSeenAt
      if (gapMs >= INACTIVITY_FLOOR_MS) {
        this.writeRecord({ registrantId, lastSeenAt: now })
        return { shouldFire: true, gapSeconds: Math.round(gapMs / 1000) }
      }

      return { shouldFire: false, gapSeconds: null }
    } catch (error) {
      logger.error(error)
      return { shouldFire: false, gapSeconds: null }
    }
  }

  // lastSeenAt records when the user last LEFT the app (hidden/pagehide/blur),
  // so the floor measures a true inactivity gap
  public static markLeave(registrantId: number): void {
    try {
      this.writeRecord({ registrantId, lastSeenAt: Date.now() })
    } catch (error) {
      logger.error(error)
    }
  }

  public static isAuthExchangeRoute(pathname: string | null): boolean {
    return !!pathname && AUTH_EXCHANGE_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`))
  }

  public static markPushEntry(): void {
    this.pushEntryPending = true
  }

  public static consumePushEntry(): boolean {
    const pending = this.pushEntryPending
    this.pushEntryPending = false
    return pending
  }

  // context-scoped marker: this browsing context authenticated through the
  // token flow (auth-link landing page or the sign-in waiting page's socket)
  public static markAuthLinkEntry(): void {
    try {
      sessionStorage.setItem(AUTH_LINK_ENTRY_KEY, '1')
    } catch (error) {
      logger.error(error)
    }
  }

  public static hasAuthLinkEntry(): boolean {
    try {
      return sessionStorage.getItem(AUTH_LINK_ENTRY_KEY) === '1'
    } catch {
      return false
    }
  }

  // §4.2 precedence: push > standalone > auth_link > browser. Pure — the
  // one-shot push marker must be consumed by the caller SYNCHRONOUSLY with the
  // evaluateOpen decision (before any await), so a racing evaluation cannot
  // eat it while this one resolves isStandalone.
  public static resolveEntry(isStandalone: boolean, isPushEntry: boolean): AppOpenEntry {
    if (isPushEntry) return 'push'
    if (isStandalone) return 'standalone'
    if (this.hasAuthLinkEntry()) return 'auth_link'
    return 'browser'
  }

  // resolves from the CDN-loaded A2HS library with a 3-second timeout, falling
  // back to matchMedia — never suppressing the event (§4.2)
  public static async resolveIsStandalone(): Promise<boolean> {
    try {
      const deadline = Date.now() + IS_STANDALONE_TIMEOUT_MS
      while (Date.now() < deadline) {
        if (typeof window !== 'undefined' && window.AddToHomeScreenInstance) {
          return !!window.AddToHomeScreenInstance.isStandAlone()
        }
        await new Promise((resolve) => setTimeout(resolve, IS_STANDALONE_POLL_MS))
      }
      return typeof window !== 'undefined' && !!window.matchMedia?.('(display-mode: standalone)').matches
    } catch {
      return false
    }
  }

  // Synchronous variant for events fired from user interaction (share, shop now, A2HS, wizard
  // steps): by then the A2HS script has almost always loaded; otherwise matchMedia answers for
  // installed PWAs on every platform. Never throws — false on any failure.
  public static resolveIsStandaloneSync(): boolean {
    try {
      if (typeof window === 'undefined') return false
      if (window.AddToHomeScreenInstance) return !!window.AddToHomeScreenInstance.isStandAlone()
      return !!window.matchMedia?.('(display-mode: standalone)').matches
    } catch {
      return false
    }
  }

  private static readRecord(): SessionRecord | null {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return null
    try {
      const record = JSON.parse(raw) as SessionRecord
      if (typeof record?.registrantId !== 'number' || typeof record?.lastSeenAt !== 'number') return null
      return record
    } catch {
      return null
    }
  }

  private static writeRecord(record: SessionRecord): void {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(record))
  }

  public static resetForTesting(): void {
    this.pushEntryPending = false
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
      sessionStorage.removeItem(AUTH_LINK_ENTRY_KEY)
    } catch {
      // ignore
    }
  }
}
